import React, { createContext, useCallback, useContext, useState } from 'react';
import './ToastNotifications.css';

export type ToastType = 'info' | 'success' | 'error';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  dismissible: boolean;
}

interface ToastContextValue {
  showToast: (type: ToastType, message: string, options?: { dismissible?: boolean; autoDismissMs?: number }) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return ctx;
};

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  return (
    <div className={`toast toast-${toast.type}`} role="status">
      <span className="toast-message">{toast.message}</span>
      {toast.dismissible && (
        <button
          type="button"
          className="toast-dismiss"
          aria-label="Dismiss"
          onClick={() => onDismiss(toast.id)}
        >
          ×
        </button>
      )}
    </div>
  );
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (type: ToastType, message: string, options?: { dismissible?: boolean; autoDismissMs?: number }) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const dismissible = options?.dismissible ?? type !== 'info';
      setToasts((prev) => [...prev, { id, type, message, dismissible }]);

      const autoMs = options?.autoDismissMs ?? (type === 'info' ? 4000 : undefined);
      if (autoMs != null && autoMs > 0) {
        window.setTimeout(() => removeToast(id), autoMs);
      }
    },
    [removeToast]
  );

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {toasts.length > 0 && (
        <div className="toast-container">
          {toasts.map((toast) => (
            <ToastItem key={toast.id} toast={toast} onDismiss={removeToast} />
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
};
